import axios from 'axios';
import type { AxiosError } from 'axios';
import type { RegisteredRouter } from '@tanstack/react-router';
import { CancelledError } from '@tanstack/react-query';
import { getBaseUrl } from '@/lib/api/utils.ts';
import { resetSession } from '@/lib/api/auth.ts';

declare module 'axios' {
  interface AxiosRequestConfig {
    skipAuthRedirect?: boolean;
  }
}

export const api = axios.create({
  baseURL: getBaseUrl(),
  withCredentials: true,
  headers: {
    Accept: 'application/json',
  },
});

/**
 * Detects the "not logged in" signal used by the backend.
 *
 * At 2026-02-11 django-rest-framework reports a missing session as a 403 with a
 * specific `detail` message rather than a 401 (401 is reserved for login
 * failures), so we match that exact shape.
 */
export function isUnauthorizedAxiosError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.response?.status !== 403) return false;
  const detail = (error.response.data as { detail?: unknown } | undefined)?.detail;
  return detail === 'Authentication credentials were not provided.';
}

/**
 * Whether an error is "expected" — an operational condition, not a frontend bug —
 * and should therefore NOT be reported to Sentry. Used by the error boundaries and
 * the Sentry `beforeSend` net.
 *
 * Expected: TanStack Query cancellations; any axios error with no server response
 * (network / offline / timeout / cancelled request); auth failures (401
 * login-failure, 403 session-gone); and server-side 5xx. Everything else —
 * unexpected 4xx (400/404/409/…) and other JS errors — is a potential bug and is
 * kept.
 */
export function isExpectedError(error: unknown): boolean {
  if (error instanceof CancelledError) return true;
  if (!axios.isAxiosError(error)) return false;
  if (!error.response) return true;
  const { status } = error.response;
  return status === 401 || status === 403 || status >= 500;
}

/**
 * Registers a single response interceptor that redirects to `/login` whenever a
 * request fails because the session is gone. Call once, after the router exists.
 *
 * Requests that need to handle the unauthorized case themselves (e.g. the
 * initial `fetchCurrentUser` probe in the `_logged` route guard) opt out with
 * `{ skipAuthRedirect: true }`.
 */
export function setupApiInterceptors(router: RegisteredRouter) {
  api.interceptors.response.use(undefined, (error: AxiosError) => {
    if (isUnauthorizedAxiosError(error) && !error.config?.skipAuthRedirect) {
      // The session is gone — drop the stale identity so it can't be served
      // from cache (e.g. on a back-navigation) before redirecting to login.
      resetSession(router.options.context.queryClient);
      void router.navigate({ to: '/login' });
    }
    return Promise.reject(error);
  });
}
